import {computed} from 'mobx';
import {BaseModuleState} from "../../../infrastructure/models/base-module-state";
import {BaseMobxState} from "../../../infrastructure/mobx/base-mobx-state";
import {NavigationManager} from "../../../core/managers/navigation-manager";
import {AppStore} from "../../../core/app/app-store";
import {LoggerUtils} from "../../../infrastructure/utils/logger/logger-utils";
import {DatetimeUtils} from "../../../infrastructure/utils/datetime-utils";
import {DeviceViewModel} from "../../../core/models/device.view-model";
import {TermsUtils} from "../../../infrastructure/utils/terms-utils";
import {RelatedEntitiesWidgetConfig} from "../related-entities-widget.config";
import {COMPONENT_STATES} from "../../../core/consts";
import type {EntityViewModel} from "../../../infrastructure/models/entity/entity.view-model";
import {BaseDomainModel} from "../../../infrastructure/models/base-domain-model";
import {CompetitionDomainModel} from "../../../domain/common/competition/models/competition-domain-model";
import {CompetitorDomainModel} from "../../../domain/common/competitor/models/competitor-domain-model";
import {AthleteDomainModel} from "../../../domain/common/athlete/athlete-domain-model";
import {RelatedEntitiesWidgetAthleteViewModel} from "./related-entities-widget.athlete-view-model";
import {RelatedEntitiesWidgetCompetitionViewModel} from "./related-entities-widget.competition-view-model";
import {RelatedEntitiesWidgetCompetitorViewModel} from "./related-entities-widget.competitor-view-model";
import {SearchDataSource} from "../../../domain/search/models/search.data-source";

export class RelatedEntitiesWidgetState extends BaseModuleState {
  dataSource: SearchDataSource;

  static injectables = [...BaseMobxState.injectables, NavigationManager, AppStore, LoggerUtils, DatetimeUtils, DeviceViewModel, TermsUtils, RelatedEntitiesWidgetConfig];

  get isPopulated(): Boolean {
    return this.componentState !== COMPONENT_STATES.loaded || this.entities?.length > 0;
  }

  get isAthleteContext(): String {
    return this.props.athleteId;
  }

  get isCompetitorContext(): String {
    return this.props.competitorId;
  }

  get isCompetitionContext(): String {
    return this.props.copmetitionId;
  }

  get title(): String {
    return this.terms.WEB_RELATED_ENTITIES;
  }

  get athleteTypeViewOrder(): Array<String> {
    return {
       RelatedEntitiesWidgetAthleteViewModel: 1,
       RelatedEntitiesWidgetCompetitorViewModel: 2,
       RelatedEntitiesWidgetCompetitionViewModel: 3
     };
  }

  get competitorTypeViewOrder(): Array<String> {
    return {
      RelatedEntitiesWidgetCompetitorViewModel: 1,
      RelatedEntitiesWidgetAthleteViewModel: 2,
      RelatedEntitiesWidgetCompetitionViewModel: 3
    };
  }

  get competitionTypeViewOrder(): Array<String> {
    return {
      RelatedEntitiesWidgetCompetitionViewModel: 1,
      RelatedEntitiesWidgetCompetitorViewModel: 2,
      RelatedEntitiesWidgetAthleteViewModel: 3
    };
  }

  comparator(typeOrder: Array<String>, currentId: string = "") {
    return (entityA: EntityViewModel, entityB: EntityViewModel) => {
      const typeComparison = typeOrder[entityA.objectType] - typeOrder[entityB.objectType];

      if (typeComparison !== 0) {
        return typeComparison;
      }

      if (entityA.objectType === "RelatedEntitiesWidgetCompetitorViewModel" && entityB.objectType === "RelatedEntitiesWidgetCompetitorViewModel") {
        if (entityA.id === currentId && entityB.id !== currentId) {
          return -1;
        } else if (entityA.id !== currentId && entityB.id === currentId) {
          return 1;
        }
      }

      return 0;
    };
  }

  @computed get entities():Array<EntityViewModel> {
    let athletes: Array<EntityViewModel> = this.dataSource?.athletes?.map((result: BaseDomainModel) => this.createViewModelByType(result)) || [];
    let competitions: Array<EntityViewModel> = this.dataSource?.competitions?.map((result: BaseDomainModel) => this.createViewModelByType(result)) || [];
    let competitors: Array<EntityViewModel> = this.dataSource?.competitors?.map((result: BaseDomainModel) => this.createViewModelByType(result)) || [];

    let entities = [...athletes, ...competitions, ...competitors];
    if (entities && this.isAthleteContext) {
      entities = entities.sort(this.comparator(this.athleteTypeViewOrder));
    } else if (entities && this.isCompetitorContext) {
      const currentId = this.props.competitorId;
      entities = entities.sort(this.comparator(this.competitorTypeViewOrder, currentId));
    } else if (entities && this.isCompetitionContext) {
      entities = entities.sort(this.comparator(this.competitionTypeViewOrder));
    }
     return entities;
  }

  createViewModelByType(entity: BaseDomainModel): EntityViewModel {
    const entitiesTypes = {
      [CompetitionDomainModel.name]: RelatedEntitiesWidgetCompetitionViewModel,
      [CompetitorDomainModel.name]: RelatedEntitiesWidgetCompetitorViewModel,
      [AthleteDomainModel.name]: RelatedEntitiesWidgetAthleteViewModel,
    }

    const result: EntityViewModel = new entitiesTypes[entity.objectType](entity, this.isLightIcons);

    return result;
  }
}
